/**
 * SUPPORT-ONLY. Serialize/deserialize the OCPP timelog page state to/from URL
 * query params so support users can share a link that reproduces the exact view.
 *
 * URL scheme (minimal — mode is inferred from which ID is present):
 *  - receipt mode     : receiptId
 *  - transaction mode : txnId
 *  - session mode     : sessionId
 *  - charger mode     : chargerId + from + to + optional focusFrom/focusTo/connector
 *
 * `from`/`to` in charger mode store TimeSpec-encoded strings when the user chose
 * a relative or "now" bound (e.g. "~1d-ago", "now"), falling back to ISO strings
 * for absolute timestamps — so shared links preserve the picker mode.
 * Existing ISO-string URLs remain fully backwards-compatible.
 */
import { decodeTimeSpecString, resolveTimeSpec } from './components/TimeRangePicker.component';
import type { LookupMode, TimelogState } from './types';

/**
 * Every query param this page owns. Used to clear stale timelog params on each
 * update while leaving everything else (notably `companyId`, which the support
 * route guard requires) untouched.
 */
export const TIMELOG_PARAM_KEYS = [
  'chargerId',
  'from',
  'to',
  'receiptId',
  'focusFrom',
  'focusTo',
  'connector',
  'txnId',
  'sessionId',
] as const;

/** Infer the lookup mode from which ID params are present. */
const inferMode = (params: URLSearchParams): LookupMode | undefined => {
  if (params.get('receiptId')?.trim()) return 'receipt';
  if (params.get('txnId')?.trim()) return 'transaction';
  if (params.get('sessionId')?.trim()) return 'session';
  if (params.get('chargerId')?.trim()) return 'charger';
  return undefined;
};

/** Read page state out of the URL search params (all fields optional). */
export const decodeState = (params: URLSearchParams): Partial<TimelogState> => {
  const str = (k: string) => params.get(k)?.trim() || undefined;
  const num = (k: string) => {
    const v = params.get(k);
    if (v == null || v.trim() === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const mode = inferMode(params);
  if (!mode) return {};

  if (mode === 'receipt') return { mode, receiptId: str('receiptId') };
  if (mode === 'transaction') return { mode, txnId: str('txnId') };
  if (mode === 'session') return { mode, sessionId: str('sessionId') };

  // Charger mode — decode from/to as TimeSpec strings, then resolve to ISO for
  // the API call while preserving the original encoded form for the picker UI.
  const fromRaw = str('from');
  const toRaw = str('to');
  const resolveRaw = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    const spec = decodeTimeSpecString(raw);
    if (!spec) return raw; // unrecognized — pass through as-is
    return resolveTimeSpec(spec).toISOString();
  };

  return {
    mode,
    chargerId: str('chargerId'),
    from: resolveRaw(fromRaw),
    to: resolveRaw(toRaw),
    fromSpec: fromRaw,
    toSpec: toRaw,
    focusFrom: str('focusFrom'),
    focusTo: str('focusTo'),
    connector: num('connector'),
  };
};

/** Build a minimal param record from page state, dropping empty values. */
export const encodeState = (state: Partial<TimelogState>): Record<string, string> => {
  const set = (out: Record<string, string>, k: string, v: string | number | undefined) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (s) out[k] = s;
  };

  if (state.mode === 'receipt') {
    const out: Record<string, string> = {};
    set(out, 'receiptId', state.receiptId);
    return out;
  }
  if (state.mode === 'transaction') {
    const out: Record<string, string> = {};
    set(out, 'txnId', state.txnId);
    return out;
  }
  if (state.mode === 'session') {
    const out: Record<string, string> = {};
    set(out, 'sessionId', state.sessionId);
    return out;
  }

  // charger mode (default)
  const out: Record<string, string> = {};
  set(out, 'chargerId', state.chargerId);
  // Prefer the TimeSpec-encoded form (e.g. "~1d-ago", "now") when available so
  // relative/now mode survives link sharing; fall back to the resolved ISO.
  set(out, 'from', state.fromSpec ?? state.from);
  set(out, 'to', state.toSpec ?? state.to);
  set(out, 'focusFrom', state.focusFrom);
  set(out, 'focusTo', state.focusTo);
  set(out, 'connector', state.connector);
  return out;
};

/**
 * Merge page state into the existing query string, preserving every param this
 * page doesn't own (e.g. `companyId`). Returns a fresh URLSearchParams.
 */
export const mergeStateIntoParams = (
  prev: URLSearchParams,
  state: Partial<TimelogState>,
): URLSearchParams => {
  const next = new URLSearchParams(prev);
  TIMELOG_PARAM_KEYS.forEach((k) => next.delete(k));
  Object.entries(encodeState(state)).forEach(([k, v]) => next.set(k, v));
  return next;
};
